import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Badge,
  Modal,
  Button,
  Table,
  InputGroup,
  FormControl,
  Toast,
  ToastContainer,
  Spinner,
  Image,
  Pagination,
} from "react-bootstrap";
import Card from "../../../components/Card";
import {
  FaEye,
  FaClock,
  FaUserSlash,
  FaUserCheck,
  FaBriefcase,
  FaTrash,
  FaBox,
  FaClipboardCheck,
  FaRunning,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "../../../components/auth/context/context";
import {
  getVendors,
  deleteVendor,
  updateUserStatus,
} from "../../../services/service";
import { toast } from "react-toastify";

/* Badge colour helper */
const badgeColor = {
  Verified: "bg-success",
  Pending: "bg-warning",
  Rejected: "bg-danger",
};

const getStatusVariant = (status) => {
  switch (status) {
    case "Pending":
      return "secondary";
    case "In-progress":
      return "info";
    case "Live":
      return "primary";
    case "Complete":
      return "success";
    case "Cancelled":
      return "danger";
    default:
      return "warning";
  }
};

const getBilingualText = (obj, field) => {
  try {
    if (!obj || typeof obj !== "object") return "N/A";

    const value = obj[field];
    if (!value) return "N/A";

    if (typeof value === "string") return value.trim() || "N/A";
    if (typeof value === "object") {
      const en = value.en?.trim?.() || "";
      const hi = value.hi?.trim?.() || "";
      if (en && hi) return `${en} (${hi})`;
      return en || hi || "N/A";
    }

    return value.toString?.() || "N/A";
  } catch {
    return "N/A";
  }
};
const getInitials = (name) => {
  let en = "";

  if (typeof name === "string") {
    en = name;
  } else if (typeof name === "object" && name !== null) {
    en = name.en || name.hi || "";
  }

  if (!en || typeof en !== "string") return "";

  const trimmed = en.trim();
  if (!trimmed) return "";

  const words = trimmed.split(/\s+/);
  return words.length === 1
    ? words[0][0].toUpperCase()
    : words.map((w) => w[0].toUpperCase()).join("");
};

const Avatar = ({ src, name, size = 40 }) => {
  const [imgError, setImgError] = useState(false);

  // Normalize name (it can be string, object, or null)
  let displayName = "Vendor";

  if (typeof name === "string" && name.trim() !== "") {
    displayName = name.trim();
  } else if (typeof name === "object" && name !== null) {
    displayName = name.en || name.hi || "Vendor";
  }

  const initials = displayName
    .split(" ")
    .map((word) => word[0]?.toUpperCase())
    .join("")
    .slice(0, 2);

  const avatarStyle = {
    width: size,
    height: size,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#007bff",
    color: "white",
    fontWeight: "bold",
    fontSize: size > 40 ? "18px" : "14px",
  };

  return src && !imgError ? (
    <Image
      src={src}
      alt={displayName}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        objectFit: "cover",
      }}
      onError={() => setImgError(true)}
    />
  ) : (
    <div style={avatarStyle}>{initials || "V"}</div>
  );
};

const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return null;
  return `${process.env.REACT_APP_IMAGE_URL}${profilePic}`;
};

const VendorList = () => {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showEquipModal, setShowEquipModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [showTotalJobsModal, setShowTotalJobsModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showReceivedModal, setShowReceivedModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [showDeleteRestriction, setShowDeleteRestriction] = useState(false);
  const [restrictedJobStatus, setRestrictedJobStatus] = useState("");
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [vendorToToggle, setVendorToToggle] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
  };
  const debouncedSearch = useDebounce(filterText, 500);
  const fetchVendors = async (page = 1, startOverride = null, endOverride = null) => {
    try {
      setLoading(true);
      const sDate = startOverride !== null ? startOverride : startDate;
      const eDate = endOverride !== null ? endOverride : endDate;

      const response = await getVendors({
        page,
        limit: 10,
        search: debouncedSearch,
        startDate: sDate || "",
        endDate: eDate || "",
      });

      if (response.success) {
        const vendorsWithLocation = response.data.users.map((vendor) => {
          const completedJobs = (vendor.jobs || [])
            .filter((j) => j.status === "Complete")
            .map((j) => ({
              jobId: j.jobId,
              customer: getBilingualText(j.customer, "name"),
              earnings: j.price || 0,
              status: j.status,
              paymentStatus: j.paymentStatus || "Pending",
            }));
          const liveJobs = (vendor.jobs || [])
            .filter((j) =>
              ["In-progress", "live", "extended"].includes(j.status)
            )
            .map((j) => ({
              status: j.status,
              jobId: j.jobId,
              customer: getBilingualText(j.customer, "name"),
              startedAt: j.scheduled?.dateTime
                ? new Date(j.scheduled?.dateTime).toLocaleString("en-IN", {
                  hour12: true,
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  second: "numeric",
                })
                : "N/A",
            }));

          const cancelledJobs = (vendor.jobs || [])
            .filter((j) => j.status === "Cancelled")
            .map((j) => ({
              status: j.status,
              jobId: j.jobId,
              customer: getBilingualText(j.customer, "name"),
              cancelledAt: j.scheduled?.dateTime
                ? new Date(j.scheduled?.dateTime).toLocaleString("en-IN", {
                  hour12: true,
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  second: "numeric",
                })
                : "N/A",
            }));

          const pendingJobs = (vendor.jobs || [])
            .filter((j) => j.status === "Pending")
            .map((j) => ({
              status: j.status,
              jobId: j.jobId,
              customer: getBilingualText(j.customer, "name"),
              createdAt: j.createdAt
                ? new Date(j.createdAt).toLocaleString("en-IN", {
                  hour12: true,
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  second: "numeric",
                })
                : "N/A",
            }));

          const totalJobs = [
            ...completedJobs,
            ...liveJobs,
            ...pendingJobs,
            ...cancelledJobs,
          ];
          const kycStatus = vendor.vendorKyc
            ? vendor.vendorKyc.status
            : "pending";

          const pendingPayments = (vendor.jobs || [])
            .filter((j) => j.paymentStatus === "Pending")
            .reduce((sum, j) => sum + Number(j.price || 0), 0);

          const paymentsReceived = (vendor.jobs || [])
            .filter((j) => j.paymentStatus === "Paid")
            .reduce((sum, j) => sum + Number(j.price || 0), 0);

          // === SAFE NAME LOGIC ===
          let displayName = "N/A";
          let nameEn = "N/A";
          let nameHi = "";
          let nameObj = {};

          if (vendor.name) {
            if (typeof vendor.name === "string") {
              displayName = vendor.name;
              nameEn = vendor.name;
            } else if (typeof vendor.name === "object") {
              nameObj = vendor.name;
              nameEn = nameObj.en || "N/A";
              nameHi = nameObj.hi || "";
              displayName = nameHi ? `${nameEn} (${nameHi})` : nameEn;
            }
          }

          return {
            id: vendor._id,
            name: displayName,
            nameObj,
            nameEn,
            phone: vendor.phone || "N/A",
            email: vendor.email || "N/A",
            profilePic: vendor.profilePic || null,
            location: getBilingualText(vendor, "address"),
            equipmentList: vendor.equipment || [],
            totalJobs,
            completedJobs,
            liveJobs,
            cancelledJobs,
            pendingJobs,
            pendingPayments,
            paymentsReceived,
            rating: vendor.rating || 0,
            kyc: kycStatus,
            blacklist: vendor.blacklist,
            createdAt: vendor.createdAt || vendor._id,
            jobs: vendor.jobs || [],
            walletBalance: vendor?.walletBalance || 0,
            gstNumber: vendor.gstNumber || "N/A",

          };
        });

        vendorsWithLocation.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setVendors(vendorsWithLocation);
        setTotalPages(response.data.totalPages);
      } else {
        setError(response.message || "Failed to fetch vendors");
        setShowToast(true);
      }
    } catch (err) {
      setError(err.message || "An error occurred");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);
  useEffect(() => {
    fetchVendors(currentPage);
  }, [currentPage, debouncedSearch]);

  const getKycBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge bg="success">Approved</Badge>;
      case "done":
        return <Badge bg="info">Done</Badge>;
      case "rejected":
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="warning">Pending</Badge>;
    }
  };
  const handleViewVendor = (vendor) => {
    setCurrentVendor(vendor);
    setShowVendorModal(true);
  };

  const handleEquipClick = (vendor) => {
    setCurrentVendor(vendor);
    setShowEquipModal(true);
  };

  const handleCompletedClick = (vendor) => {
    setCurrentVendor(vendor);
    setShowCompletedModal(true);
  };

  const handleLiveClick = (vendor) => {
    setCurrentVendor(vendor);
    setShowLiveModal(true);
  };

  const handleCancelledClick = (vendor) => {
    setCurrentVendor(vendor);
    setShowCancelledModal(true);
  };
  const toggleBlock = (vendor) => {
    setVendorToToggle(vendor);
    setShowBlockConfirm(true);
  };

  const confirmToggleBlock = async () => {
    try {
      const id = vendorToToggle.id;
      const newStatus = !vendorToToggle.blacklist;
      await updateUserStatus(id, newStatus);

      setVendors((prev) =>
        prev.map((v) => (v.id === id ? { ...v, blacklist: newStatus } : v))
      );

      setShowBlockConfirm(false);
      setVendorToToggle(null);

      setError(null);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
      if (newStatus === true) {
        toast.success("Vendor has been successfully blocked.");
      } else {
        toast.success("Vendor has been successfully unblocked.");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      // setError("Failed to update vendor status!");
      toast.error("Failed to update vendor status!");
      setShowToast(true);
    }
  };
  const handleDelete = (vendor) => {
    const restrictedJob = vendor.jobs.find((j) =>
      ["In-progress", "live", "extended"].includes(j.status)
    );

    if (restrictedJob) {
      setRestrictedJobStatus(restrictedJob.status);
      setVendorToDelete(vendor);
      setShowDeleteRestriction(true);
      return;
    }

    setVendorToDelete(vendor);
    setShowDeleteConfirm(true);
  };
  const confirmDeleteVendor = async () => {
    try {
      await deleteVendor(vendorToDelete.id);
      setVendors((prev) => prev.filter((v) => v.id !== vendorToDelete.id));
      setShowDeleteConfirm(false);
      setVendorToDelete(null);
      toast.success("Vendor deleted successfully!");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete vendor!");
    }
  };

  const currentEntries = vendors;
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return <FaSort />;
    return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const downloadVendorCSV = async () => {
    try {
      setDownloading(true);
      const response = await getVendors({
        page: 1,
        limit: 100000,
        search: debouncedSearch,
        startDate: startDate || "",
        endDate: endDate || "",
      });

      if (response.success && response.data.users) {
        const csvRows = [
          [
            "Name",
            "Phone",
            "Email",
            "Location",
            "KYCStatus",
            "TotalJobs",
            "CompletedJobs",
            "LiveJobs",
            "PendingJobs",
            "CancelledJobs",
            "PendingPayments",
            "PaymentsReceived",
            "Rating",
            "WalletBalance",
            "JoinedDate",
          ],
        ];

        response.data.users.forEach((vendor) => {
          // Calculate stats (similar to fetchVendors)
          const completedJobsCount = (vendor.jobs || []).filter(
            (j) => j.status === "Complete"
          ).length;
          const liveJobsCount = (vendor.jobs || []).filter((j) =>
            ["In-progress", "live", "extended"].includes(j.status)
          ).length;
          const pendingJobsCount = (vendor.jobs || []).filter(
            (j) => j.status === "Pending"
          ).length;
          const cancelledJobsCount = (vendor.jobs || []).filter(
            (j) => j.status === "Cancelled"
          ).length;
          const totalJobsCount =
            completedJobsCount +
            liveJobsCount +
            pendingJobsCount +
            cancelledJobsCount;

          const pendingPayments = (vendor.jobs || [])
            .filter((j) => j.paymentStatus === "Pending")
            .reduce((sum, j) => sum + Number(j.price || 0), 0);

          const paymentsReceived = (vendor.jobs || [])
            .filter((j) => j.paymentStatus === "Paid")
            .reduce((sum, j) => sum + Number(j.price || 0), 0);

          const kycStatus = vendor.vendorKyc
            ? vendor.vendorKyc.status
            : "pending";
          const joinedDate = vendor.createdAt
            ? new Date(vendor.createdAt).toLocaleDateString()
            : "N/A";

          // Name logic
          let displayName = "N/A";
          if (vendor.name) {
            if (typeof vendor.name === "string") {
              displayName = vendor.name;
            } else if (typeof vendor.name === "object") {
              const nameEn = vendor.name.en || "N/A";
              const nameHi = vendor.name.hi || "";
              displayName = nameHi ? `${nameEn} (${nameHi})` : nameEn;
            }
          }

          const row = [
            `"${displayName}"`,
            `"${vendor.phone || "N/A"}"`,
            `"${vendor.email || "N/A"}"`,
            `"${getBilingualText(vendor, "address")}"`,
            `"${kycStatus}"`,
            totalJobsCount,
            completedJobsCount,
            liveJobsCount,
            pendingJobsCount,
            cancelledJobsCount,
            pendingPayments,
            paymentsReceived,
            vendor.rating || 0,
            vendor.walletBalance || 0,
            `"${joinedDate}"`,
          ];
          csvRows.push(row.join(","));
        });

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `vendor_list_${new Date().toISOString()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error("Failed to fetch data for CSV export");
      }
    } catch (err) {
      console.error("CSV Download Error:", err);
      toast.error("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };



  return (
    <>
      {/* <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={5000}
          autohide
          bg="danger"
        >
          <Toast.Header>
            <FaExclamationTriangle className="me-2" />
            <strong className="me-auto">Error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{error}</Toast.Body>
        </Toast>
      </ToastContainer> */}

      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h4 className="card-title mb-0">Vendor List</h4>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <FormControl
                    type="date"
                    placeholder="Start Date"
                    value={startDate}
                    max={endDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ maxWidth: "150px" }}
                  />
                  <span className="text-muted">-</span>
                  <FormControl
                    type="date"
                    placeholder="End Date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ maxWidth: "150px" }}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCurrentPage(1);
                      fetchVendors(1);
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setCurrentPage(1);
                      fetchVendors(1, "", "");
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <InputGroup style={{ width: "250px" }}>
                  <FormControl
                    placeholder="Search by name or email..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  {loading && (
                    <InputGroup.Text>
                      <Spinner animation="border" size="sm" />
                    </InputGroup.Text>
                  )}
                </InputGroup>
                <Button
                  variant="primary"
                  onClick={downloadVendorCSV}
                  disabled={downloading}
                >
                  {downloading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Downloading...
                    </>
                  ) : (
                    "Download CSV"
                  )}
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="px-0" style={{ position: "relative", minHeight: "200px" }}>
              {loading && vendors.length === 0 ? (
                <div className="text-center my-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading vendor data...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover style={{ opacity: loading ? 0.6 : 1 }}>
                    <thead>
                      <tr>
                        <th>Profile</th>
                        <th
                          onClick={() => handleSort("name")}
                          style={{ cursor: "pointer" }}
                        >
                          Name {renderSortIcon("name")}
                        </th>
                        <th
                          onClick={() => handleSort("phone")}
                          style={{ cursor: "pointer" }}
                        >
                          Phone {renderSortIcon("phone")}
                        </th>
                        <th
                          onClick={() => handleSort("email")}
                          style={{ cursor: "pointer" }}
                        >
                          Email {renderSortIcon("email")}
                        </th>
                        <th
                          onClick={() => handleSort("location")}
                          style={{ cursor: "pointer" }}
                        >
                          Location {renderSortIcon("location")}
                        </th>
                        <th
                          onClick={() => handleSort("equipments")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaBox className="me-1" /> Equip.{" "}
                          {renderSortIcon("equipments")}
                        </th>
                        <th>KYC</th>
                        <th
                          onClick={() => handleSort("completedJobs")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaBriefcase className="me-1" /> Total Jobs{" "}
                          {renderSortIcon("completedJobs")}
                        </th>
                        <th
                          onClick={() => handleSort("liveJobs")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaRunning className="me-1" /> Live Jobs{" "}
                          {renderSortIcon("liveJobs")}
                        </th>
                        <th
                          onClick={() => handleSort("pendingJobs")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaClock className="me-1" /> Pending{" "}
                          {renderSortIcon("pendingJobs")}
                        </th>
                        <th
                          onClick={() => handleSort("cancelledJobs")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaExclamationTriangle className="me-1" /> Cancelled{" "}
                          {renderSortIcon("cancelledJobs")}
                        </th>
                        <th
                          onClick={() => handleSort("completedJobs")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaClipboardCheck className="me-1" /> Complete Jobs{" "}
                          {renderSortIcon("completedJobs")}
                        </th>
                        <th
                          onClick={() => handleSort("pendingPayments")}
                          style={{ cursor: "pointer" }}
                        >
                          Pending ₹ {renderSortIcon("pendingPayments")}
                        </th>
                        <th
                          onClick={() => handleSort("paymentsReceived")}
                          style={{ cursor: "pointer" }}
                        >
                          Received ₹ {renderSortIcon("paymentsReceived")}
                        </th>
                        <th
                          onClick={() => handleSort("rating")}
                          style={{ cursor: "pointer" }}
                        >
                          Rating {renderSortIcon("rating")}
                        </th>
                        <th>Wallet Balance</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentEntries.length > 0 ? (
                        currentEntries.map((v) => (
                          <tr key={v.id}>
                            <td>
                              <Avatar
                                src={getProfilePicUrl(v.profilePic)}
                                name={v.nameEn || v.nameObj || v.name || "Vendor"}
                                size={40}
                              />
                            </td>
                            <td>
                              <div>
                                <strong>{v.name}</strong>
                              </div>
                              {v.nameObj?.hi ? (
                                <div className="text-muted small">
                                  {v.nameObj.hi}
                                </div>
                              ) : null}
                            </td>
                            <td>{v.phone}</td>
                            <td>{v.email}</td>
                            <td>{v.location}</td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleEquipClick(v)}
                              >
                                {v.equipmentList?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <span>{getKycBadge(v.kyc)}</span>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => {
                                  setCurrentVendor(v);
                                  setShowTotalJobsModal(true);
                                }}
                              >
                                {v.totalJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleLiveClick(v)}
                              >
                                {v.liveJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => {
                                  setCurrentVendor(v);
                                  setShowPendingModal(true);
                                }}
                              >
                                {v.pendingJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleCancelledClick(v)}
                              >
                                {v.cancelledJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleCompletedClick(v)}
                              >
                                {v.completedJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => {
                                  setCurrentVendor(v);
                                  setShowPendingModal(true);
                                }}
                              >
                                ₹{Number(v.pendingPayments || 0).toLocaleString()}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => {
                                  setCurrentVendor(v);
                                  setShowReceivedModal(true);
                                }}
                              >
                                ₹
                                {Number(v.paymentsReceived || 0).toLocaleString()}
                              </Button>
                            </td>
                            <td>
                              <Badge bg="secondary">
                                {(Number(v.rating) || 0).toFixed(1)} stars
                              </Badge>
                            </td>
                            <td>₹{v?.walletBalance}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Button
                                  variant="link"
                                  className="text-info p-0 me-2"
                                  onClick={() => handleViewVendor(v)}
                                >
                                  <FaEye size={18} />
                                </Button>
                                <Button
                                  variant="link"
                                  className="text-warning p-0 me-2"
                                  onClick={() => toggleBlock(v)}
                                >
                                  {v.blacklist ? (
                                    <FaUserSlash size={18} />
                                  ) : (
                                    <FaUserCheck size={18} />
                                  )}
                                </Button>
                                <Button
                                  variant="link"
                                  className="text-danger p-0"
                                  title="Delete"
                                  onClick={() => handleDelete(v)}
                                >
                                  <FaTrash size={18} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="16" className="text-center py-4">
                            {error
                              ? `Error loading vendors: ${error}`
                              : "No vendors found"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
              <div className="d-flex justify-content-center my-3">
                <Pagination>
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  />

                  {/* Show first page if not in window */}
                  {currentPage > 3 && (
                    <>
                      <Pagination.Item onClick={() => setCurrentPage(1)}>
                        1
                      </Pagination.Item>
                      {currentPage > 4 && <Pagination.Ellipsis disabled />}
                    </>
                  )}

                  {/* Window of pages around current page */}
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    if (page >= currentPage - 2 && page <= currentPage + 2) {
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    }
                    return null;
                  })}

                  {/* Show last page if not in window */}
                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <Pagination.Ellipsis disabled />
                      )}
                      <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}

                  <Pagination.Next
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showTotalJobsModal}
        onHide={() => setShowTotalJobsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Total Jobs</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.totalJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.totalJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>{job.customer || "N/A"}</td>
                        <td>
                          <Badge bg={getStatusVariant(job.status)}>
                            {job.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No jobs found.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowTotalJobsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Vendor Detail Modal */}
      <Modal
        show={showVendorModal}
        onHide={() => setShowVendorModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Vendor Details</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            <Row className="mb-3">
              <Col md="6">
                <div className="d-flex align-items-center mb-3">
                  <Avatar
                    src={getProfilePicUrl(currentVendor.profilePic)}
                    name={currentVendor.name}
                    size={80}
                  />
                  <div className="ms-3">
                    <h5>{currentVendor.name}</h5>
                    <span className={`badge ${badgeColor[currentVendor.kyc]}`}>
                      {currentVendor.kyc}
                    </span>
                  </div>
                </div>
                <p>
                  <strong>Phone:</strong> {currentVendor.phone}
                </p>
                <p>
                  <strong>Email:</strong> {currentVendor.email}
                </p>
                <p>
                  <strong>Location:</strong> {currentVendor.location}
                </p>
                <p>
                  <strong>Rating:</strong>{" "}
                  {(Number(currentVendor.rating) || 0).toFixed(1)} stars
                </p>
                <p>
                  <strong>GST Number:</strong>{" "}
                  {currentVendor.gstNumber || "N/A"}
                </p>
              </Col>
              <Col md="6">
                <p>
                  <strong>Total Equipments:</strong>{" "}
                  {currentVendor.equipmentList?.length || 0}
                </p>
                <p>
                  <strong>Total job:</strong> {currentVendor.jobs?.length || 0}
                </p>
                <p>
                  <strong>Jobs Completed:</strong>{" "}
                  {currentVendor.completedJobs?.length || 0}
                </p>
                <p>
                  <strong>Live Jobs:</strong>{" "}
                  {currentVendor.liveJobs?.length || 0}
                </p>
                <p>
                  <strong>Pending Payments (₹):</strong>{" "}
                  {(currentVendor.pendingPayments || 0).toLocaleString()}
                </p>
                <p>
                  <strong>Payments Received (₹):</strong>{" "}
                  {(currentVendor.paymentsReceived || 0).toLocaleString()}
                </p>
                <p>
                  <strong>Wallet Balance (₹):</strong>{" "}
                  {(currentVendor.walletBalance || 0).toLocaleString()}
                </p>
              </Col>
            </Row>
          </Modal.Body>
        )}
      </Modal>

      {/* Equipment List Modal */}
      <Modal
        show={showEquipModal}
        onHide={() => setShowEquipModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Equipment List</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.equipmentList?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>KycStatus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.equipmentList.map((eq, i) => (
                      <tr key={i}>
                        <td>{eq._id}</td>
                        <td>
                          {getBilingualText(eq, "equipmentName") || "N/A"}
                        </td>
                        <td>{eq.kycStatus}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No equipment available.</p>
            )}
          </Modal.Body>
        )}
      </Modal>

      {/* Completed Jobs Modal */}
      <Modal
        show={showCompletedModal}
        onHide={() => setShowCompletedModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Completed Jobs</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.completedJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Price (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.completedJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>{job.customer || "N/A"}</td>
                        <td>₹{job.earnings.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No completed jobs found.</p>
            )}
          </Modal.Body>
        )}
      </Modal>
      {/* Live Jobs Modal */}
      <Modal
        show={showLiveModal}
        onHide={() => setShowLiveModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Live Jobs</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.liveJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.liveJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>{job.customer || "N/A"}</td>
                        <td>{job.startedAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No live jobs at the moment.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLiveModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pending Jobs Modal */}
      <Modal
        show={showPendingModal}
        onHide={() => setShowPendingModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Pending Jobs</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.pendingJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.pendingJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>{job.customer || "N/A"}</td>
                        <td>{job.createdAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No pending jobs found.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPendingModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancelled Jobs Modal */}
      <Modal
        show={showCancelledModal}
        onHide={() => setShowCancelledModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancelled Jobs</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.cancelledJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.cancelledJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>{job.customer || "N/A"}</td>
                        <td>{job.cancelledAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No cancelled jobs found.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelledModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Restriction Modal */}
      <Modal
        show={showDeleteRestriction}
        onHide={() => setShowDeleteRestriction(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cannot Delete Vendor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This vendor has a <strong>{restrictedJobStatus}</strong> job. Vendors
          with <em>In-progress</em>, <em>Live</em>, or <em>extended</em> jobs
          cannot be deleted.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteRestriction(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{vendorToDelete?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteVendor}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pending Payments Modal */}
      <Modal
        show={showPendingModal}
        onHide={() => setShowPendingModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Pending Payments</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.jobs?.filter((j) => j.paymentStatus === "Pending")
              .length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Amount (₹)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.jobs
                      .filter((j) => j.paymentStatus === "Pending")
                      .map((job, i) => (
                        <tr key={i}>
                          <td>{job.jobId}</td>
                          <td>
                            {getBilingualText(job.customer, "name") || "N/A"}
                          </td>
                          <td>₹{job.price || 0}</td>
                          <td>
                            <Badge bg="secondary">{job.paymentStatus}</Badge>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No pending payments.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPendingModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Payments Received Modal */}
      <Modal
        show={showReceivedModal}
        onHide={() => setShowReceivedModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Payments Received</Modal.Title>
        </Modal.Header>
        {currentVendor && (
          <Modal.Body>
            {currentVendor.jobs?.filter((j) => j.paymentStatus === "Paid")
              .length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Customer</th>
                      <th>Amount (₹)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVendor.jobs
                      .filter((j) => j.paymentStatus === "Paid")
                      .map((job, i) => (
                        <tr key={i}>
                          <td>{job.jobId}</td>
                          <td>
                            {getBilingualText(job.customer, "name") || "N/A"}
                          </td>
                          <td>₹{job.price || 0}</td>
                          <td>
                            <Badge bg="success">{job.paymentStatus}</Badge>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No payments received.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReceivedModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showBlockConfirm}
        onHide={() => setShowBlockConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {vendorToToggle?.blacklist ? "Unblock Vendor" : "Block Vendor"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p style={{ fontSize: "18px" }}>
            Are you sure you want to{" "}
            <strong>{vendorToToggle?.blacklist ? "unblock" : "block"}</strong>{" "}
            this vendor?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBlockConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant={vendorToToggle?.blacklist ? "success" : "danger"}
            onClick={confirmToggleBlock}
          >
            {vendorToToggle?.blacklist ? "Unblock" : "Block"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VendorList;
